import React from "react";
import {
  mdiAlertCircle,
  mdiCheckCircle,
  mdiClockOutline,
  mdiInformationOutline,
  mdiMinusCircle,
  mdiPlay,
} from "@mdi/js";
import MdiIcon from "../../components/device/app/MdiIcon";
import AppSwitch from "../../components/layout/app/AppSwitch";
import { lastRunText, summaryText, whenText } from "../../automation/format";
import { isColoured, lookOf, pillOf } from "../../automation/look";

const LEGACY_FROM = { rule: "Rule Engine", scene: "Scenes", routine: "Schedules" };

// ▶ Run, for tap-to-run automations. On a coloured card it is white with the
// card's colour.
export const RunButton = ({ onRun, cardColor, small = false, label }) => (
  <button
    type="button"
    className={`hx-auto-run ${small ? "is-small" : ""} ${cardColor ? "is-on-card" : ""}`}
    style={cardColor ? { "--hx-auto-card": cardColor } : undefined}
    onClick={(event) => {
      event.stopPropagation();
      onRun();
    }}
    aria-label={label}
  >
    <MdiIcon path={mdiPlay} size={16} />
    Run
  </button>
);

// The on/off switch of an automation. On a coloured card it is white with the
// card's colour, like the app's Rule Engine cards.
export const AutomationSwitch = ({ automation, onToggle, cardColor }) => (
  <AppSwitch
    checked={automation.enabled !== false}
    onChange={onToggle}
    label={`${automation.name}: ${automation.enabled !== false ? "on" : "off"}`}
    knobColor={cardColor || undefined}
    trackColor={cardColor ? "#ffffff" : undefined}
  />
);

// What shows under the summary: why it's paused or in review, how its last
// run went, or when it runs next.
const CardNote = ({ automation, coloured }) => {
  if (automation.status === "review") {
    const from = LEGACY_FROM[automation.legacy?.kind];
    return (
      <span className="hx-auto-note">
        <MdiIcon path={mdiInformationOutline} size={15} />
        {automation.statusReason || `Moved from ${from || "the old screens"}, check it before turning it on`}
      </span>
    );
  }
  if (automation.status === "paused") {
    return (
      <span className="hx-auto-note">
        <MdiIcon path={mdiInformationOutline} size={15} />
        Paused: {automation.statusReason || "it needs an owner to save it again"}
      </span>
    );
  }
  if (automation.readOnly) {
    return (
      <span className="hx-auto-note">
        <MdiIcon path={mdiInformationOutline} size={15} />
        Set in the device's settings
      </span>
    );
  }
  const lastRun = lastRunText(automation.lastRun);
  if (lastRun) {
    const status = automation.lastRun.status;
    const icon = status === "failed" ? mdiAlertCircle : status === "skipped" ? mdiMinusCircle : mdiCheckCircle;
    return (
      <span className={`hx-auto-note is-${status} ${coloured ? "is-coloured" : ""}`}>
        <MdiIcon path={icon} size={15} />
        {lastRun}
      </span>
    );
  }
  if (automation.nextRunAt && automation.enabled !== false) {
    return (
      <span className="hx-auto-note">
        <MdiIcon path={mdiClockOutline} size={15} />
        Next: {whenText(automation.nextRunAt)}
      </span>
    );
  }
  return <span />;
};

// One automation on the Automation tab (app/screens/Automation/AutomationCard.js):
// coloured while it's on, plain when it's off, paused or done. color is the
// card colour the list gives it in turn.
const AutomationCard = ({ automation, lookup, color, onOpen, onToggle, onRun }) => {
  const look = lookOf(automation);
  const coloured = isColoured(automation);
  const cardColor = coloured ? color || look.color : null;
  const pill = pillOf(automation);
  const can = automation.can || {};
  const showRun = can.run && (automation.type === "tap" || (automation.type === "nfc" && !can.edit));
  const showSwitch = can.edit && !automation.readOnly && automation.type !== "tap";

  return (
    <article
      className={`hx-auto-card ${coloured ? "is-coloured" : ""}`}
      style={cardColor ? { "--hx-auto-card": cardColor } : undefined}
    >
      <div className="hx-auto-card-top">
        <h3 className="hx-auto-card-name">
          <button type="button" className="hx-auto-open" onClick={onOpen}>
            {automation.name}
          </button>
        </h3>
        {pill ? <span className={`hx-auto-pill ${pill.tone ? `is-${pill.tone}` : ""}`}>{pill.text}</span> : null}
        <span className="hx-auto-card-controls">
          {showRun ? <RunButton onRun={onRun} cardColor={cardColor} label={`Run ${automation.name}`} /> : null}
          {showSwitch ? <AutomationSwitch automation={automation} onToggle={onToggle} cardColor={cardColor} /> : null}
        </span>
      </div>
      <p className="hx-auto-card-summary">
        <MdiIcon path={look.icon} size={17} />
        <span>{summaryText(automation, lookup)}</span>
      </p>
      <div className="hx-auto-card-bottom">
        <CardNote automation={automation} coloured={coloured} />
        {automation.createdBy ? <span className="hx-auto-by">by {automation.createdBy}</span> : null}
      </div>
    </article>
  );
};

export default AutomationCard;
